import type { PushSubscriptionInput } from './api';

/**
 * Browser push plumbing (REWRITE_PLAN.md §10 Phase 7).
 *
 * Kept apart from the hook so the subscription dance — permission, VAPID key
 * decoding, reuse of an existing subscription — is testable and has one home.
 * Every function degrades to null/undefined rather than throwing when the
 * browser does not support push, because the app must work without it.
 */

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** VAPID keys arrive URL-safe base64; the Push API wants raw bytes. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * The active service worker, or null when none is registered yet.
 *
 * `getRegistration()` rather than `navigator.serviceWorker.ready`: `ready` never
 * resolves when no worker is registered (the dev server does not register one),
 * which would leave a caller awaiting forever.
 */
export async function activeRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await activeRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Return the existing subscription or create one. Reusing it matters: calling
 * `subscribe` again with a different key can leave two subscriptions on the
 * device, only one of which the server knows about.
 */
export async function ensureSubscription(publicKey: string): Promise<PushSubscription | null> {
  const reg = await activeRegistration();
  if (!reg) return null;
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
  });
}

/** Flatten a browser subscription into the shape the API stores. */
export function subscriptionInput(sub: PushSubscription): PushSubscriptionInput | null {
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null;
  return { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth };
}
